import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { contentFor, type Package } from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button } from "@/shared/ui/core";
import { AdminGuard, DataTableFrame, Pagination } from "@/features/admin/components";
import { ArchivePackageDialog } from "./components/ArchivePackageDialog";
import { PackagesListBody } from "./components/PackagesListBody";
import { PackagesToolbar } from "./components/PackagesToolbar";
import { useAdminPackages, useArchivePackage } from "./packages.queries";
import { useApiErrorMessage } from "./useApiErrorMessage";
import {
  applyFilterChange,
  DEFAULT_FILTERS,
  hasActiveFilters,
  sortPackages,
  toQuery,
  type PackageFilters,
} from "./filters";

/**
 * El catálogo de paquetes del panel.
 *
 * `AdminGuard` envuelve la pantalla ENTERA en vez de comprobar el rol
 * dentro: así, cuando alguien no tiene permiso, el componente de abajo
 * ni siquiera se monta y no llega a pedir `/api/admin/packages` — una
 * petición que el servidor rechazaría, pero que igualmente aparecería en
 * los registros como un intento de acceso que nadie hizo a propósito.
 *
 * Y conviene repetirlo: eso es COMODIDAD, no seguridad. La puerta la
 * pone el servidor.
 */
export function AdminPackagesPage() {
  return (
    <AdminGuard>
      <PackageCatalogScreen />
    </AdminGuard>
  );
}

function PackageCatalogScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<PackageFilters>(DEFAULT_FILTERS);
  const [archiveTarget, setArchiveTarget] = useState<Package | null>(null);

  const query = useMemo(() => toQuery(filters), [filters]);

  const packages = useAdminPackages(query);
  const archive = useArchivePackage();
  const archiveError = useApiErrorMessage(archive.error);

  const total = packages.data?.total ?? 0;
  const filtered = hasActiveFilters(filters);

  /*
   * El orden se aplica sobre la página que devolvió el servidor: el
   * contrato no declara un parámetro de orden (ver `filters.ts`). Va en
   * un `useMemo` porque `sortPackages` copia el array, y sin memoria una
   * lista nueva en cada render remontaría la tabla entera.
   */
  const items = useMemo(
    () => sortPackages(packages.data?.items ?? [], filters.sort),
    [packages.data?.items, filters.sort],
  );

  function changeFilters(patch: Partial<PackageFilters>) {
    setFilters((current) => applyFilterChange(current, patch));
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    try {
      await archive.mutateAsync(archiveTarget.id);
      setArchiveTarget(null);
    } catch {
      // El error se enseña arriba, en su aviso. El diálogo se queda
      // abierto: cerrarlo daría por hecho que funcionó.
    }
  }

  const locale = i18n.language.startsWith("en") ? "en" : "es";
  const archiveTargetName = archiveTarget
    ? contentFor(archiveTarget.translations, locale).name
    : null;

  return (
    <>
      <PageHeader
        title={t("Paquetes")}
        description={t("{{total}} paquetes en el catálogo.", { total })}
        actions={
          <Button
            size="sm"
            leftIcon={<Plus />}
            onClick={() => navigate("/admin/packages/new")}
          >
            {t("Nuevo paquete")}
          </Button>
        }
        // Sin `divider`: la tabla que viene debajo ya tiene su propio borde superior, y dos
        // líneas seguidas se leen como un error de maquetación (Anahí, 26-ago).
      />

      {archive.isError && (
        <Alert
          variant="error"
          className="mb-4"
          title={t("No se pudo archivar el paquete")}
          onDismiss={() => archive.reset()}
        >
          {archiveError}
        </Alert>
      )}

      <DataTableFrame aria-busy={packages.isFetching}>
        <PackagesToolbar
          filters={filters}
          canReset={filtered}
          onChange={changeFilters}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />

        <PackagesListBody
          packages={items}
          loading={packages.isPending}
          error={packages.error}
          filtered={filtered}
          onRetry={() => void packages.refetch()}
          onCreate={() => navigate("/admin/packages/new")}
          onResetFilters={() => setFilters(DEFAULT_FILTERS)}
          onEdit={(pkg) => navigate(`/admin/packages/${pkg.id}`)}
          onArchive={(pkg) => {
            archive.reset();
            setArchiveTarget(pkg);
          }}
          archivingId={archive.isPending ? (archiveTarget?.id ?? null) : null}
        />

        {total > 0 && (
          <Pagination
            page={filters.page}
            perPage={filters.perPage}
            total={total}
            onPageChange={(page) => changeFilters({ page })}
            onPerPageChange={(perPage) => changeFilters({ perPage, page: 1 })}
          />
        )}
      </DataTableFrame>

      <ArchivePackageDialog
        packageName={archiveTargetName}
        loading={archive.isPending}
        onConfirm={() => void confirmArchive()}
        onCancel={() => setArchiveTarget(null)}
      />
    </>
  );
}
